// src/components/admin/settings/EvenementSettings.jsx
import React, { useState, useEffect } from 'react';
import DataTableSettings from '../../common/DataTableSettings';
import ModalComponent from '../../common/ModalComponent';
import { AlertCircle, Calendar, MapPin, Users, Building2, Pill, Bell, FileText, Paperclip, X, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { evenementsApi } from '../../../api/evenements-api';
import { toast } from 'react-toastify';

const EvenementSettings = () => {
  const [evenements, setEvenements] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [laboratories, setLaboratories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentEvenement, setCurrentEvenement] = useState(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    location: '',
    conclusions: '',
    reminderAlert: false,
    comment: '',
    associationId: '',
    laboratoryId: '',
    selectedProducts: [],
    attachments: ''
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    // Fetch real data from API
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch all required data in parallel
        const [evenementsData, associationsData, laboratoriesData] = await Promise.all([
          evenementsApi.getEvenements(),
          evenementsApi.getAssociations(),
          evenementsApi.getLaboratories()
        ]);
        
        // Transform evenements data for display
        const transformedEvenements = evenementsData.map(evenement => ({
          ...evenement,
          associationName: evenement.association?.nomAssociation || 'Non définie',
          laboratoryName: evenement.laboratory?.nom || 'Non défini',
          productsCount: evenement.products?.length || 0,
          status: getEventStatus(evenement.date, evenement.conclusions)
        }));
        
        setEvenements(transformedEvenements);
        setAssociations(associationsData);
        setLaboratories(laboratoriesData);
      } catch (error) {
        console.error('Error fetching evenements:', error);
        toast.error('Erreur lors du chargement des événements');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter products based on selected laboratory
  useEffect(() => {
    if (formData.laboratoryId) {
      const selectedLab = laboratories.find(lab => lab.id === formData.laboratoryId);
      setFilteredProducts(selectedLab?.products || []);
    } else {
      setFilteredProducts([]);
    }
  }, [formData.laboratoryId, laboratories]);

  // Helper function to determine event status
  const getEventStatus = (eventDate, conclusions) => {
    const now = new Date();
    const date = new Date(eventDate);
    
    if (date > now) {
      return { text: 'À venir', className: 'bg-blue-100 text-blue-800' };
    } else if (conclusions) {
      return { text: 'Terminé', className: 'bg-green-100 text-green-800' };
    } else {
      return { text: 'En attente', className: 'bg-yellow-100 text-yellow-800' };
    }
  };

  const columns = [
    {
      Header: 'Date & Heure',
      accessor: 'date',
      sortable: true,
      searchable: false,
      Cell: ({ value }) => (
        <div className="flex items-center">
          <Calendar size={16} className="mr-2 text-blue-500" />
          <div>
            <div className="font-medium">
              {format(new Date(value), 'dd MMMM yyyy', { locale: fr })}
            </div>
            <div className="text-xs text-gray-500">
              {format(new Date(value), 'HH:mm', { locale: fr })}
            </div>
          </div>
        </div>
      )
    },
    {
      Header: 'Lieu',
      accessor: 'location',
      sortable: true,
      searchable: true,
      Cell: ({ value }) => (
        <div className="flex items-center">
          <MapPin size={16} className="mr-1 text-red-500" />
          <span className="truncate max-w-xs" title={value}>{value}</span>
        </div>
      )
    },
    {
      Header: 'Association',
      accessor: 'associationName',
      sortable: true,
      searchable: true,
      Cell: ({ value }) => (
        <div className="flex items-center">
          <Users size={16} className="mr-1 text-purple-500" />
          <span className="truncate max-w-xs" title={value}>{value}</span>
        </div>
      )
    },
    {
      Header: 'Laboratoire',
      accessor: 'laboratoryName',
      sortable: true,
      searchable: true,
      Cell: ({ value }) => (
        <div className="flex items-center">
          <Building2 size={16} className="mr-1 text-green-500" />
          <span>{value}</span>
        </div>
      )
    },
    {
      Header: 'Produits',
      accessor: 'productsCount',
      sortable: true,
      searchable: false,
      Cell: ({ value }) => (
        <div className="flex items-center">
          <Pill size={16} className="mr-1 text-orange-500" />
          <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
            {value}
          </span>
        </div>
      )
    },
    {
      Header: 'Statut',
      accessor: 'status',
      sortable: false,
      searchable: false,
      Cell: ({ value }) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${value.className}`}>
          {value.text}
        </span>
      )
    },
    {
      Header: 'Rappel',
      accessor: 'reminderAlert',
      sortable: false,
      searchable: false,
      Cell: ({ value }) => (
        <div className="flex items-center justify-center">
          {value ? (
            <Bell size={16} className="text-yellow-500" title="Rappel activé" />
          ) : (
            <span className="text-gray-300">-</span>
          )}
        </div>
      )
    }
  ];

  const handleCreateNew = () => {
    setCurrentEvenement(null);
    setFormData({
      date: '',
      time: '',
      location: '',
      conclusions: '',
      reminderAlert: false,
      comment: '',
      associationId: '',
      laboratoryId: '',
      selectedProducts: [],
      attachments: ''
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleEdit = (evenement) => {
    setCurrentEvenement(evenement);
    
    const eventDate = new Date(evenement.date);
    const dateStr = format(eventDate, 'yyyy-MM-dd');
    const timeStr = format(eventDate, 'HH:mm');
    
    setFormData({
      date: dateStr,
      time: timeStr,
      location: evenement.location || '',
      conclusions: evenement.conclusions || '',
      reminderAlert: evenement.reminderAlert || false,
      comment: evenement.comment || '',
      associationId: evenement.associationId || '',
      laboratoryId: evenement.laboratoryId || '',
      selectedProducts: evenement.products?.map(p => p.id) || [],
      attachments: ''
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleDelete = (evenement) => {
    setCurrentEvenement(evenement);
    setIsConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setIsSubmitting(true);
      
      // Delete evenement via API
      await evenementsApi.deleteEvenement(currentEvenement.id);
      
      // Update local state
      setEvenements(evenements.filter(e => e.id !== currentEvenement.id));
      setIsConfirmDeleteOpen(false);
      
      toast.success('Événement supprimé avec succès');
    } catch (error) {
      console.error('Error deleting evenement:', error);
      toast.error('Erreur lors de la suppression de l\'événement');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    
    // Clear error when field is filled
    if (value && formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }

    // Reset products when laboratory changes
    if (name === 'laboratoryId') {
      setFormData(prev => ({ ...prev, selectedProducts: [] }));
    }
  };

  const handleProductSelection = (productId) => {
    setFormData(prev => ({
      ...prev,
      selectedProducts: prev.selectedProducts.includes(productId)
        ? prev.selectedProducts.filter(id => id !== productId)
        : [...prev.selectedProducts, productId]
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.date) errors.date = 'La date est requise';
    if (!formData.time) errors.time = 'L\'heure est requise';
    if (!formData.location) errors.location = 'Le lieu est requis';
    if (!formData.associationId) errors.associationId = 'L\'association est requise';
    if (!formData.laboratoryId) errors.laboratoryId = 'Le laboratoire est requis';
    
    // Check if date and time are in the future for new events
    if (formData.date && formData.time && !currentEvenement) {
      const eventDateTime = new Date(`${formData.date}T${formData.time}`);
      const now = new Date();
      
      if (eventDateTime <= now) {
        errors.date = 'La date et l\'heure doivent être dans le futur';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Combine date and time
      const eventDateTime = new Date(`${formData.date}T${formData.time}`);
      
      const evenementData = {
        date: eventDateTime.toISOString(),
        location: formData.location,
        conclusions: formData.conclusions || null,
        reminderAlert: formData.reminderAlert,
        comment: formData.comment || null,
        associationId: formData.associationId,
        laboratoryId: formData.laboratoryId,
        productIds: formData.selectedProducts,
        attachments: formData.attachments || null
      };
      
      let savedEvenement;
      
      if (currentEvenement) {
        // Update existing evenement
        evenementData.id = currentEvenement.id;
        savedEvenement = await evenementsApi.updateEvenement(evenementData);
        toast.success('Événement mis à jour avec succès');
      } else {
        // Create new evenement
        savedEvenement = await evenementsApi.createEvenement(evenementData);
        toast.success('Événement créé avec succès');
      }
      
      // Transform saved evenement for display
      const selectedAssociation = associations.find(a => a.id === savedEvenement.associationId);
      const selectedLaboratory = laboratories.find(l => l.id === savedEvenement.laboratoryId);
      
      const transformedEvenement = {
        ...savedEvenement,
        associationName: selectedAssociation?.nomAssociation || 'Non définie',
        laboratoryName: selectedLaboratory?.nom || 'Non défini',
        productsCount: savedEvenement.products?.length || 0,
        status: getEventStatus(savedEvenement.date, savedEvenement.conclusions)
      };
      
      if (currentEvenement) {
        // Update existing evenement in local state
        setEvenements(evenements.map(e => e.id === currentEvenement.id ? transformedEvenement : e));
      } else {
        // Add new evenement to local state
        setEvenements([...evenements, transformedEvenement]);
      }
      
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving evenement:', error);
      toast.error('Erreur lors de la sauvegarde de l\'événement');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <DataTableSettings
        title="Gestion des Événements"
        subtitle="Planifiez et gérez les événements avec les associations et laboratoires"
        data={evenements}
        columns={columns}
        loading={isLoading}
        onCreate={handleCreateNew}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      
      {/* Create/Edit Modal */}
      <ModalComponent
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={currentEvenement ? 'Modifier l\'événement' : 'Créer un nouveau événement'}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        size="xl"
      >
        <div className="space-y-6">
          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de l'événement
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  formErrors.date ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {formErrors.date && (
                <p className="mt-1 text-sm text-red-600">{formErrors.date}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Heure
              </label>
              <input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  formErrors.time ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {formErrors.time && (
                <p className="mt-1 text-sm text-red-600">{formErrors.time}</p>
              )}
            </div>
          </div>
          
          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lieu de l'événement
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                formErrors.location ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Hôtel Hyatt Regency, Casablanca"
            />
            {formErrors.location && (
              <p className="mt-1 text-sm text-red-600">{formErrors.location}</p>
            )}
          </div>
          
          {/* Association and Laboratory */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Association
              </label>
              <select
                name="associationId"
                value={formData.associationId}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  formErrors.associationId ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Sélectionner une association</option>
                {associations.map(association => (
                  <option key={association.id} value={association.id}>
                    {association.nomAssociation}
                  </option>
                ))}
              </select>
              {formErrors.associationId && (
                <p className="mt-1 text-sm text-red-600">{formErrors.associationId}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Laboratoire
              </label>
              <select
                name="laboratoryId"
                value={formData.laboratoryId}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  formErrors.laboratoryId ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Sélectionner un laboratoire</option>
                {laboratories.map(laboratory => (
                  <option key={laboratory.id} value={laboratory.id}>
                    {laboratory.nom}
                  </option>
                ))}
              </select>
              {formErrors.laboratoryId && (
                <p className="mt-1 text-sm text-red-600">{formErrors.laboratoryId}</p>
              )}
            </div>
          </div>
          
          {/* Products Selection */}
          {formData.laboratoryId && filteredProducts.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Produits à présenter
              </label>
              <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3">
                <div className="space-y-2">
                  {filteredProducts.map(product => (
                    <label key={product.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.selectedProducts.includes(product.id)}
                        onChange={() => handleProductSelection(product.id)}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{product.name}</span>
                      {product.price && (
                        <span className="ml-auto text-xs text-gray-500">
                          {product.price.toFixed(2)} DH
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Sélectionnez les produits qui seront présentés lors de cet événement
              </p>
            </div>
          )}
          
          {/* Conclusions and Comment */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Conclusions de l'événement
              </label>
              <textarea
                name="conclusions"
                value={formData.conclusions}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Résumé des conclusions et retours de l'événement..."
              />
              <p className="mt-1 text-xs text-gray-500">
                À remplir après l'événement
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Commentaires additionnels
              </label>
              <textarea
                name="comment"
                value={formData.comment}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Notes et observations..."
              />
            </div>
          </div>
          
          {/* Reminder Alert */}
          <div className="flex items-center">
            <input
              id="reminderAlert"
              name="reminderAlert"
              type="checkbox"
              checked={formData.reminderAlert}
              onChange={handleInputChange}
              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
            />
            <label htmlFor="reminderAlert" className="ml-2 flex items-center text-sm text-gray-700">
              <Bell size={16} className="mr-1 text-yellow-500" />
              Activer les rappels pour cet événement
            </label>
          </div>
          
          {/* Attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Documents annexes
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                <Paperclip className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="file-upload-evenement"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-green-700 hover:text-green-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-500"
                  >
                    <span>Ajouter des fichiers</span>
                    <input
                      id="file-upload-evenement"
                      name="attachments"
                      type="file"
                      className="sr-only"
                      multiple
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          attachments: Array.from(e.target.files).map(f => f.name).join(', ')
                        }));
                      }}
                    />
                  </label>
                  <p className="pl-1">ou glisser-déposer</p>
                </div>
                <p className="text-xs text-gray-500">PDF, DOCX, Images jusqu'à 10MB chacun</p>
                {formData.attachments && (
                  <p className="text-sm text-green-700">{formData.attachments}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </ModalComponent>
      
      {/* Confirm Delete Modal */}
      <ModalComponent
        isOpen={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        title="Confirmer la suppression"
        onSubmit={confirmDelete}
        isSubmitting={isSubmitting}
        submitLabel="Supprimer"
        submitButtonClassName="bg-red-600 hover:bg-red-700 focus:ring-red-500"
      >
        <div className="flex items-center space-x-4 text-red-600 mb-4">
          <AlertCircle size={24} />
          <h3 className="text-lg font-medium">Attention</h3>
        </div>
        <p className="text-gray-500">
          Êtes-vous sûr de vouloir supprimer l'événement du{' '}
          <strong>{currentEvenement && format(new Date(currentEvenement.date), 'dd MMMM yyyy à HH:mm', { locale: fr })}</strong> ?
        </p>
        <p className="text-gray-500 mt-2">
          Cette action est irréversible. Toutes les informations associées à cet événement seront perdues.
        </p>
        {currentEvenement && (
          <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-700">
            <p className="font-medium">Détails de l'événement :</p>
            <ul className="list-disc ml-5 mt-1">
              <li>Lieu: {currentEvenement.location}</li>
              <li>Association: {currentEvenement.associationName}</li>
              <li>Laboratoire: {currentEvenement.laboratoryName}</li>
              <li>Produits: {currentEvenement.productsCount}</li>
            </ul>
          </div>
        )}
      </ModalComponent>
    </div>
  );
};

export default EvenementSettings;